import { Breadcrumb, ProgressIndicator, Modal, CardSkeletonLoader, ErrorBoundary } from '@/components/atoms'
import { PageHeader, SaveCancelFooter } from '@/components/molecules'
import { PageContainer } from '@/components/organisms'
import { useAppSelector } from '@/store'
import { useState, useMemo } from 'react'
import { useCreatePointMultiplier } from '@/pages/PointMultiplier/CreatePointMultiplier-hook'
import { ReviewPointMultiplierSection } from '@/pages/PointMultiplier/ReviewPointMultiplierSection'
import { usePointMultiplier } from '@/hooks/usePointMultiplier'
import { useParams } from 'react-router-dom'
import { MODAL_TYPE } from '@/utils/constants/globalStyle'

export const ReviewPointMultiplier = () => {
    const pointMultiplier = useAppSelector(state => state?.pointMultiplier)
    const [formData, setFormData] = useState(pointMultiplier)
    const {id} = useParams()
    const isReadOnly = !id;

    // Dynamic breadcrumb logic
    const breadcrumbs = useMemo(() => {
        const baseBreadcrumbs = [
            { label: '', href: '/' },
            { label: 'Point Multiplier', href: '/rewards/point-multiplier' }
        ];

        // Check if we have an existing point multiplier (either from URL id or data uuid/name)
        const hasExistingMultiplier = id || pointMultiplier?.uuid;
        const multiplierName = pointMultiplier?.modifierName || pointMultiplier?.multiplierName;

        // Creation flow - no id in URL and no existing data
        if (!hasExistingMultiplier) {
            return [
                ...baseBreadcrumbs,
                { label: 'Create Point Multiplier', href: '/rewards/point-multiplier' }
            ];
        }

        // Review/Edit flow - id exists in URL or data has uuid/name
        const displayName = multiplierName || 'Loading...';
        return [
            ...baseBreadcrumbs,
            { label: displayName, href: '/rewards/point-multiplier' }
        ];
    }, [id, pointMultiplier?.modifierName, pointMultiplier?.multiplierName, pointMultiplier?.uuid]);

    const {
        formError,
        setFormError,
        rulesCriteriaJsonSchema,
        rulesCriteriaUISchema,
        handleFormChange,
        validateFormData,
        disableNext,
        setDisableNext,
        formFields,
        createReqObj,
        progressBarSteps,
        onClickPrimaryButton,
        onClickSecondaryButton,
        isCancel,
        handleCancel
    } = useCreatePointMultiplier()

    const {
        handleDraft: handlePointMultiplierDraft,
        handlePublish: handlePointMultiplierPublish,
        loading
    } = usePointMultiplier()

    const handleDraft = async () => {
        const reqObj = createReqObj('draft');
        const draftResponse = await handlePointMultiplierDraft(reqObj)
        if (!draftResponse?.success) {
            return
        }
    }
    const handlePublish = async () => {
        const isValidForm = validateFormData();
        if (!isValidForm) {
            return
        }
        const reqObj = createReqObj('Published');
        const publishResponse = await handlePointMultiplierPublish(reqObj)
        if (!publishResponse?.success) {
            return
        }
    }


    return (
        <ErrorBoundary>
            <div className="salesTargetsContainer flex flex-col" data-testid="review-point-multiplier">
                <Breadcrumb
                    uiSchema={{
                        isDark: true,
                        className: 'mb-5',
                        breadcrumbs: breadcrumbs,
                    }}
                    id={'pm'}
                />
                <PageHeader title="Create Point Multiplier" data-testid="page-header" />

                {isReadOnly && (
                    <div className="grid p-8">
                        <div className="w-full justify-self-center">
                            <ProgressIndicator
                                currentStep={progressBarSteps.length}
                                completed={false}
                                isDark={false}
                                steps={progressBarSteps?.length}
                                titles={progressBarSteps || []}
                                data-testid="progress-indicator"
                            />
                        </div>
                    </div>
                )}

                <PageContainer data-testid="page-container">
                {loading? <CardSkeletonLoader/>:<>
                    <ReviewPointMultiplierSection
                        isReview={true}
                        formData={formData}
                        setFormData={setFormData}
                        rulesCriteriaJsonSchema={rulesCriteriaJsonSchema}
                        rulesCriteriaUISchema={rulesCriteriaUISchema}
                        formError={formError}
                        setFormError={setFormError}
                        formFields={formFields}
                        disableNext={disableNext}
                        setDisableNext={setDisableNext}
                        handleFormChange={handleFormChange}
                        pointMultiplierId={id}
                    />
                    {isCancel ? (
                        <Modal
                            type={MODAL_TYPE.FLUID}
                            showModal={isCancel}
                            onCloseModal={handleCancel}
                            title="Are you sure you want to cancel?"
                            text={'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'}
                            showPrimaryButton={true}
                            primaryButtonText="Yes"
                            onClickPrimaryButton={onClickPrimaryButton}
                            showSecondaryButton={true}
                            secondaryButtonText="No"
                            onClickSecondaryButton={onClickSecondaryButton}
                            footerProps={{
                                size: 'medium',
                                type: 'primary',
                            }}
                            isDark={true}
                        />
                    ) : null}
                    </>
                }

                </PageContainer>

                {isReadOnly ? (
                    <SaveCancelFooter
                        isReview={true}
                        handleCancel={handleCancel}
                        nextAction={handlePublish}
                        draftAction={handleDraft}
                        hideSaveDraft={false}
                        data-testid="save-cancel-footer"
                    />
              ): null}
            </div>
        </ErrorBoundary>
    )
}